import {
  AppBar,
  AppBarSection,
  AppBarSpacer,
} from "@progress/kendo-react-layout";
import "./header.css";
import { useEffect, useState, useRef } from "react";
import { gxDateTimeFormat } from "@/app/utils/helpers/gxDateTimeFormat";
import MyProfile from "@/app/page/default/userProfile/myProfile/MyProfile";
import { useToken } from "@/app/services/context/TokenContext";
import { redirectToGrowXLogout } from "@/app/utils/loginservice/redirectToGrowXLogout";
import { useT } from "@/app/hooks/useT";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/app/redux/store";
import { useUserDetails } from "@/app/services/context/UserDetailsContext";
import { FiCheck, FiCopy } from "react-icons/fi";
import NotificationAlert from "@/app/page/default/userProfile/notification&Alert/notificationAlert";
import { handleNotification } from "@/app/redux/reducers/userSlice";
import { fetchUnreadNotifications } from "@/app/redux/reducers/notificationsMessage";
import i18n from "@/i18n/i18n";
import { ICON_BASE_PATH } from "@/app/utils/config";
import { useCompanyLogo } from "@/app/hooks/useCompanyLogo";
import AvatarDisplay from "@/app/page/default/userProfile/myProfile/AvatarDisplay";

const Header = () => {
  const dispatch = useDispatch<AppDispatch>();
  const t = useT();
  const userData = useSelector((state: RootState) => state.userData.data);
  const { openNotification } = useSelector((state: RootState) => state.users);
  const { personalInfo } = useUserDetails();
  const { logout } = useToken();
  const location = useLocation();
  const hideHeaderTitle =
    ["/CFO/Dashboard", "/CFO/genieAI"].includes(location.pathname) ||
    location.pathname.startsWith("/CFO/Payables") ||
    location.pathname.startsWith("/CFO/Receivables");

  const languageOptions = ["English", "Arab"] as const;
  const [languageChanged, setLanguageChanged] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    localStorage.getItem("appLanguage") || "English",
  );

  // ---------------------------handleLanguageChange--------------------------------------

  const handleLanguageChange = () => {
    const current = languageOptions.indexOf(selectedLanguage as any);
    const nextLang = languageOptions[(current + 1) % languageOptions.length];
    setSelectedLanguage(nextLang);
    setLanguageChanged(true);
    setTimeout(() => setLanguageChanged(false), 1000);
  };

  // ---------------------------useEffect--------------------------------------

  useEffect(() => {
    localStorage.setItem("appLanguage", selectedLanguage);
  }, [selectedLanguage]);

  // ---------------------------useEffect--------------------------------------
  useEffect(() => {
    const langMap: Record<string, string> = {
      English: "en",
      Arab: "arab",
    };
    i18n.changeLanguage(langMap[selectedLanguage] || "en");
  }, [selectedLanguage]);

  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const [isMyProfileOpen, setIsMyProfileOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<number>(0);

  // ---------------------------notifications--------------------------------------

  const dashboardUnread = useSelector(
    (state: RootState) => state?.notifications?.dashboardUnread,
  );
  const unreadCount = dashboardUnread?.length;

  useEffect(() => {
    dispatch(fetchUnreadNotifications());
  }, [dispatch]);

  useEffect(() => {
    if (!openNotification) {
      dispatch(fetchUnreadNotifications());
    }
  }, [openNotification, dispatch]);

  // ---------------------------useEffect--------------------------------------

  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (
        profileRef.current &&
        !profileRef.current.contains(e.target as Node)
      ) {
        setProfileOpen(false);
      }
    };
    if (profileOpen) {
      document.addEventListener("mousedown", handleOutside);
    }
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [profileOpen]);

  // ---------------------------handleProfileClick--------------------------------------

  const handleProfileClick = () => {
    setSelectedTab(0);
    setIsMyProfileOpen(true);
    setProfileOpen(false);
  };

  // ---------------------------handleSettingsClick--------------------------------------

  const handleSettingsClick = () => {
    setSelectedTab(2);
    setIsMyProfileOpen(true);
    setProfileOpen(false);
  };

  // ---------------------------handleLogout--------------------------------------

  const handleLogout = () => {
    setTimeout(redirectToGrowXLogout, 0);
    logout();
  };

  // ---------------------------header list names--------------------------------------

  const displayName = (userData?.displayName || "--")
    .replace(/\d+/g, "")
    .replace(/[-_]/g, " ")
    .trim();
  const roleName = userData?.roleName || "--";
  const smeName = userData?.compName || "--";
  const lastLogin = gxDateTimeFormat(personalInfo?.userLastLoginTime) || "--";
  const companyID = personalInfo?.groupId || "null";
  const [copied, setCopied] = useState(false);

  // ---------------------------copyCompanyIdfuncation-------------------------------------

  const copyCompanyId = async () => {
    try {
      await navigator.clipboard.writeText(companyID);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // ........................company logo (fallback avatar)...................................

  const { companyLogo } = useCompanyLogo(personalInfo?.groupId);

  const handleOpenCloseNotification = () => {
    dispatch(handleNotification());
  };

  // ........................profile photo state...................................
  const [avatarPhoto, setAvatarPhoto] = useState<string>("");

  useEffect(() => {
    const profileView = sessionStorage.getItem("profileView");
    if (profileView) {
      setAvatarPhoto(profileView);
    }

    const handlePhotoUpdate = (e: Event) => {
      const custom = e as CustomEvent<string>;
      setAvatarPhoto(custom.detail || "");
    };

    window.addEventListener("profilePhotoUpdated", handlePhotoUpdate);
    return () =>
      window.removeEventListener("profilePhotoUpdated", handlePhotoUpdate);
  }, []);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setProfileOpen(false);
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  return (
    <>
      <AppBar className="k-appbar-top-expanded" aria-label="Application header">
        <AppBarSection className="gx-appbarsection">
          <AppBarSpacer />

          {!hideHeaderTitle && (
            <>
              <div
                className="gx-header-title flex gap-5"
                aria-label={`Welcome ${displayName}`}
              >
                <span className="gx-font-bold text-[1.5rem]">
                  {t("hi")} {displayName}
                </span>

                <div className="gx-roleIcon">
                  <img
                    src={`${ICON_BASE_PATH}roleiconmaker.svg`}
                    alt=""
                    aria-hidden="true"
                    width={24}
                    height={24}
                  />

                  <p className="gx-p12-m">{roleName}</p>
                </div>
              </div>

              <div
                className="gx-header-smename"
                aria-label={`Company name ${smeName}`}
              >
                <span className="sme-name gx-p14-b">{smeName}</span>

                {companyID !== "null" && (
                  <div
                    className="company-id-section"
                    aria-label={`Company ID ${companyID}`}
                  >
                    <span className="gx-p12-eb">{companyID}</span>

                    {copied ? (
                      <button
                        type="button"
                        className="copy-btn"
                        aria-label="Company ID copied"
                      >
                        <FiCheck aria-hidden="true" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={copyCompanyId}
                        className="copy-btn"
                        aria-label={`Copy company ID ${companyID}`}
                      >
                        <FiCopy aria-hidden="true" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {hideHeaderTitle && (
            <>
              <div
                className="gx-header-title flex gap-5"
                aria-label="CFO Co-Pilot Executive Command Center"
              >
                <span className="gx-p18-b">
                  CFO Co-Pilot - Executive Command Center
                </span>
              </div>

              <div
                className="gx-header-cfoname"
                aria-label="Live overview of cash, risk and actions"
              >
                <span className="gx-p12-m">
                  Live overview of cash, risk, and actions for ACME Holdings
                </span>
              </div>
            </>
          )}
        </AppBarSection>

        <AppBarSection>
          <div className="gx-last-time" aria-label={`Last login ${lastLogin}`}>
            <span className="gx-p12-m mt-2 text-[#00000099]">
              {t("last_login")} : {lastLogin}
            </span>
          </div>

          {/* Notifications */}
          <div className="gx-notification-header">
            <button
              type="button"
              aria-label="Open notifications"
              onClick={handleOpenCloseNotification}
              className={`bell-icon cursor-pointer absolute ${
                unreadCount > 0 ? "auto-shake" : ""
              }`}
            >
              <img
                src={`${ICON_BASE_PATH}bell.svg`}
                alt=""
                aria-hidden="true"
                width={20}
                height={20}
              />
            </button>

            {unreadCount > 0 && (
              <div
                aria-live="polite"
                className="gx_all gx_indicator bg-green-500"
                aria-label={`${unreadCount} unread notifications`}
              />
            )}
          </div>

          {/* Profile menu */}
          <div className="gx-profile-header" ref={profileRef}>
            <button
              type="button"
              aria-haspopup="menu"
              aria-controls="profile-menu"
              onClick={() => setProfileOpen((p) => !p)}
            >
              <AvatarDisplay
                profilePhoto={avatarPhoto || null}
                companyLogo={companyLogo}
                firstName={userData?.firstName}
                lastName={userData?.lastName}
                size={40}
                className="gx-avatar-header"
              />
            </button>

            {profileOpen && (
              <div id="profile-menu" role="menu" className="profile-dropdown">
                <ul aria-label="Profile menu">
                  <li aria-label="Profile" onClick={handleProfileClick}>
                    {t("user_profile")}
                  </li>
                  <li aria-label="Logout" onClick={handleLogout}>
                    {t("logout")}
                  </li>
                </ul>
              </div>
            )}
          </div>
        </AppBarSection>
      </AppBar>

      <MyProfile
        open={isMyProfileOpen}
        setOpen={setIsMyProfileOpen}
        tabSelected={selectedTab}
      />
      {openNotification && <NotificationAlert />}
    </>
  );
};

export default Header;
